<template>
  <div class="chart-container">
    <div v-if="size !== 'mini'" class="chart-legend-container" style="height: 50px;">
      <label><span class="chart-mark chart-mark-count"></span><span class="chart-legend-name">件数</span></label>
      <label style="margin-left: 10px;">
        <span class="chart-mark chart-mark-y-avg"
              :style="{ backgroundColor: lineColorSet.red.pointBackgroundColor }"></span>
        <span class="chart-legend-name">{{legendYLabel}}</span>
      </label>
    </div>
    <div class="chart-main-container">
      <chart ref="chart" chartType="bar" chartId="CompareChart" :width="size === 'mini' ? 300 : 836"
             :height="size === 'mini' ? 180 : 400"></chart>
    </div>
    <div class="chart-column-container" v-if="compareComparison.results.length > 1 && size !== 'mini'">
      <el-radio v-for="(result, index) in compareComparison.results" v-model="selectedColumnIndex" name="compare-column"
                :label="index">{{ result.name }}
      </el-radio>
    </div>
  </div>
</template>

<script lang="js">
import { GraphBarColorSet, GraphLineColorSet } from '../../defines/chart-color'
import ChartCmp from './base.vue'
import { Category } from '../../defines/const'
import CommonUtil from '../../utils/common'
import FormatUtil from '../../utils/format'

export default {
  name: 'CompareChartCmp',
  components: {
    'chart': ChartCmp
  },
  props: {
    compareComparison: {type: Object, default: () => {}},
    yName: '',
    categoryId: '',
    size: '',
    initialColumnIndex: {type: Number, default: 0}
  },
  data () {
    return {
      selectedColumnIndex: -1,
      selectedCompareResult: null,
      lineColorSet: GraphLineColorSet,
      barColorSet: GraphBarColorSet
    }
  },
  computed: {
    legendYLabel () {
      return (this.categoryId === Category.CLF
        ? `「${this.yName} = ${this.compareComparison.results[this.selectedColumnIndex].name}」の発生率`
        : `「${this.yName}」の平均値`)
    }
  },
  watch: {
    selectedColumnIndex (newIndex) {
      if (newIndex < 0) return
      this.selectedCompareResult = this.compareComparison.results[newIndex]
    },
    selectedCompareResult (compareResult) {
      if (!compareResult) return

      const categoryId = this.categoryId
      const isCategoryClassifier = (categoryId === Category.CLF)
      const xLabel = isCategoryClassifier ? '予測確率' : '予測値'
      const yLabel = isCategoryClassifier
        ? `「${this.yName} = ${this.selectedCompareResult.name}」の発生率`
        : `「${this.yName}」の平均値`
      const yAvg = compareResult.value.map(value => value.yAvg)
      const yCount = compareResult.value.map(value => value.yCount)
      const order = compareResult.value.map(value => value.order)
      const isMini = (this.size === 'mini')

      let fraction = 0
      if (isCategoryClassifier) {
        if (yAvg.length > 1) {
          const minValue = yAvg.reduce((a, b) => a < b ? a : b)
          const maxValue = yAvg.reduce((a, b) => a > b ? a : b)
          let diffValue = Math.abs(maxValue - minValue)
          while (diffValue !== 0 && diffValue < 0.1) {
            fraction++
            diffValue *= 10
          }
        }
      }

      let datasets = []
      datasets.push(
        Object.assign(
          {
            data: yAvg,
            type: 'line',
            yAxisID: 'yAvg',
            fill: false,
            lineTension: 0,
            pointBorderWidth: isMini ? 4 : 8
          },
          this.lineColorSet.red
        )
      )
      datasets.push(Object.assign(
        {
          data: yCount,
          type: 'bar',
          yAxisID: 'count'
        },
        this.barColorSet.blue
      ))

      const chartData = {
        labels: order,
        datasets: datasets
      }

      const options = {
        responsive: isMini,
        title: {
          display: false
        },
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              id: 'count',
              position: 'left',
              scaleLabel: {
                display: !isMini,
                labelString: '件数',
                fontSize: 16
              },
              ticks: {
                beginAtZero: false,
                major: {},
                minor: {},
                callback: function (value, index, values) {
                  return CommonUtil.isEmpty(value) ? '' : `${FormatUtil.formatNumber(value)}`
                }
              }
            },
            {
              id: 'yAvg',
              position: 'right',
              scaleLabel: {
                display: !isMini,
                labelString: yLabel,
                fontSize: 16
              },
              gridLines: {
                drawOnChartArea: false
              },
              ticks: {
                beginAtZero: false,
                major: {},
                minor: {},
                callback: function (value, index, values) {
                  if (CommonUtil.isEmpty(value)) {
                    return ''
                  }

                  if (isCategoryClassifier) {
                    return FormatUtil.percentage(value, fraction) + '%'
                  } else {
                    return `${FormatUtil.formatNumber(value)}`
                  }
                }
              }
            }
          ],
          xAxes: [
            {
              display: !isMini,
              scaleLabel: {
                display: !isMini,
                labelString: xLabel,
                fontSize: 16
              },
              ticks: {
                maxRotation: 90
              }
            }
          ]
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const datasetIndex = tooltipItem.datasetIndex
              let label = data.datasets[datasetIndex].label || ''

              if (CommonUtil.isNotEmpty(label)) {
                label += ': '
              }
              if (categoryId !== Category.CLF || datasetIndex >= (datasets.length - 1)) {
                label += FormatUtil.formatNumber(tooltipItem.yLabel)
              } else {
                label += FormatUtil.percentage(tooltipItem.yLabel, fraction) + '%'
              }

              return label
            }
          }
        }
      }

      this.$refs.chart.renderChart(chartData, options)
    }
  },
  created () {
    this.selectedColumnIndex = this.initialColumnIndex
    this.$nextTick(() => {
      this.$forceUpdate()
    })
  }
}

</script>

<style scoped>
  .chart-container {
    display: flex;
    flex-direction: column;
  }

  .chart-legend-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  .chart-legend-container > label {
    display: flex;
    align-items: center;
  }

  .chart-mark {
    display: inline-block;
    width: 12px;
    height: 12px;
  }

  .chart-mark-count {
    background-color: #86C7F5;
    margin-right: 10px;
  }

  .chart-mark-y-avg {
    border-radius: 50%;
    margin-left: 10px;
  }

  .chart-legend-name {
    margin-right: 10px;
  }

  .chart-main-container {
    display: flex;
    justify-content: center;
  }
</style>



// WEBPACK FOOTER //
// src/components/chart/compare.vue